import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ShieldCheck, Mail, Lock, User } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');

  const from = location.state?.from || '/dashboard';

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success('Welcome back!');
      navigate(from);
    } else {
      setError(res.error);
    }
  };

  return (
    <AuthShell title="Welcome back" subtitle="Sign in to access your vault.">
      <form data-testid="login-form" onSubmit={submit} className="space-y-5">
        <Field icon={Mail} label="Email" testId="login-email">
          <Input
            data-testid="login-email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            placeholder="you@valutdocu.com"
            className="bg-vault-bg border-white/10"
          />
        </Field>
        <Field icon={Lock} label="Password" testId="login-password">
          <Input
            data-testid="login-password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            placeholder="••••••••"
            className="bg-vault-bg border-white/10"
          />
        </Field>
        {error && <p data-testid="login-error" className="text-sm text-red-400">{error}</p>}
        <button data-testid="login-submit" type="submit" disabled={loading} className="btn-primary w-full">
          {loading ? 'Unlocking vault…' : 'Sign in'}
        </button>
      </form>
      <p className="text-sm text-gray-400 mt-6 text-center">
        Don't have an account?{' '}
        <Link to="/register" data-testid="goto-register" className="text-vault-gold hover:underline">Create one</Link>
      </p>
    </AuthShell>
  );
}

export function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    const res = await register(name, email, password);
    setLoading(false);
    if (res.ok) {
      toast.success('Account created. Welcome to the vault.');
      navigate('/dashboard');
    } else {
      setError(res.error);
    }
  };

  return (
    <AuthShell title="Create your vault" subtitle="Free forever. No credit card required.">
      <form data-testid="register-form" onSubmit={submit} className="space-y-5">
        <Field icon={User} label="Name" testId="register-name">
          <Input
            data-testid="register-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            placeholder="Aarav Sharma"
            className="bg-vault-bg border-white/10"
          />
        </Field>
        <Field icon={Mail} label="Email" testId="register-email">
          <Input
            data-testid="register-email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            placeholder="you@valutdocu.com"
            className="bg-vault-bg border-white/10"
          />
        </Field>
        <Field icon={Lock} label="Password" testId="register-password">
          <Input
            data-testid="register-password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={6}
            placeholder="Minimum 6 characters"
            className="bg-vault-bg border-white/10"
          />
        </Field>
        {error && <p data-testid="register-error" className="text-sm text-red-400">{error}</p>}
        <button data-testid="register-submit" type="submit" disabled={loading} className="btn-primary w-full">
          {loading ? 'Building vault…' : 'Create account'}
        </button>
        <p className="text-xs text-gray-500 text-center">By signing up, you agree to our Terms & Privacy Policy.</p>
      </form>
      <p className="text-sm text-gray-400 mt-6 text-center">
        Already have an account?{' '}
        <Link to="/login" data-testid="goto-login" className="text-vault-gold hover:underline">Sign in</Link>
      </p>
    </AuthShell>
  );
}

function Field({ icon: Icon, label, children, testId }) {
  return (
    <div>
      <Label data-testid={`${testId}-label`} className="text-xs font-mono tracking-wider uppercase text-gray-400 flex items-center gap-1.5 mb-2">
        <Icon className="w-3.5 h-3.5 text-vault-gold" /> {label}
      </Label>
      {children}
    </div>
  );
}

function AuthShell({ title, subtitle, children }) {
  return (
    <div className="min-h-[calc(100vh-4rem)] vault-grid-bg flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8 group">
          <ShieldCheck className="w-8 h-8 text-vault-gold" />
          <span className="font-heading font-bold text-2xl tracking-tight">ValutDocu</span>
        </Link>
        <div className="glass-panel rounded-2xl p-8 md:p-10">
          <h1 className="text-2xl md:text-3xl font-heading font-bold tracking-tight">{title}</h1>
          <p className="text-sm text-gray-400 mt-2 mb-7">{subtitle}</p>
          {children}
        </div>
      </div>
    </div>
  );
}
